import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import Dropdown from "components/CustomDropdown/Dropdown";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import { CloudUpload, CheckCircle, EventBusy, Close } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { toast } from "react-toastify";
import dayjs from "dayjs";

const MarkAttendanceModal = ({
  open,
  onClose,
  onSubmit,
  initialData = {},
  loading = false,
  classId,
}) => {
  const [formType, setFormType] = useState("attendance"); // "attendance" or "leave"
  const [leaveFor, setLeaveFor] = useState("TUTOR"); // "TUTOR" or "PARENT"
  const [selectedLeaveDates, setSelectedLeaveDates] = useState([]); // Array for multiple dates
  const [newLeaveDate, setNewLeaveDate] = useState(null); // Temporary state for adding new date

  // New states for flexible attendance
  const [selectedDate, setSelectedDate] = useState(null); // For new attendance date
  const [selectedDates, setSelectedDates] = useState([]); // Multiple dates array
  const [selectedType, setSelectedType] = useState("REGULAR"); // Type for new attendance
  const [startTime, setStartTime] = useState(""); // Start time for new attendance
  const [endTime, setEndTime] = useState(""); // End time for new attendance

  const [formData, setFormData] = useState({
    attendanceTime: initialData.attendanceTime || new Date().toISOString(),
    topicCovered: initialData.topicCovered || "",
    homeWork: initialData.homeWork || null,
    voiceNote: initialData.voiceNote || null,
    leaveReason: "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFormData((prev) => ({ ...prev, [field]: file }));
    }
  };

  const handleSubmit = () => {
    if (formType === "attendance") {
      // Flexible mode - new attendance for any date
      if (selectedDates.length === 0) {
        toast.error("Please select at least one date");
        return;
      }

      if (!selectedType) {
        toast.error("Please select attendance type");
        return;
      }

      if (!formData.topicCovered.trim()) {
        toast.error("Please enter topics covered");
        return;
      }

      // Create flexible attendance data with multiple dates
      const submitData = {
        ...formData,
        classSessionId: classId,
        attendanceDate: selectedDates, // Send array of dates
        startTime: startTime || "", // Empty string if not provided
        endTime: endTime || "", // Empty string if not provided
        type: [selectedType],
        isFlexible: true,
      };

      console.log("Flexible attendance submit data:", submitData);
      onSubmit(submitData);
    } else {
      // Leave validation
      if (selectedLeaveDates.length === 0) {
        toast.error("Please select at least one date");
        return;
      }

      if (!leaveFor) {
        toast.error("Please select Leave For");
        return;
      }

      console.log("=== Submit Leave Data Debug ===");
      console.log("Selected dates:", selectedLeaveDates);
      console.log("Leave For:", leaveFor);
      console.log("Class ID:", classId);

      // Convert dates to ISO format
      const formattedDates = selectedLeaveDates.map((date) => date + "T00:00:00.000Z");

      // For leave, use different payload structure
      const leaveData = {
        classSessionId: classId,
        dates: formattedDates,
        leaveFor: leaveFor,
        isLeave: true,
      };
      console.log("Leave submit data:", leaveData);
      console.log("=========================");
      onSubmit(leaveData);
    }
  };

  const handleClose = () => {
    setFormData({
      attendanceTime: new Date().toISOString(),
      topicCovered: "",
      homeWork: null,
      voiceNote: null,
      leaveReason: "",
    });
    setSelectedLeaveDates([]);
    setNewLeaveDate(null);
    setFormType("attendance");
    setLeaveFor("TUTOR");
    setSelectedDate(null);
    setSelectedDates([]);
    setSelectedType("REGULAR");
    setStartTime("");
    setEndTime("");
    onClose();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "No Date";

    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 2.5,
          position: "relative",
        }}
      >
        <ArgonBox>
          <ArgonTypography variant="h5" color="white" fontWeight="bold">
            Mark Attendance / Leave
          </ArgonTypography>
          <ArgonTypography variant="caption" color="white" sx={{ opacity: 0.9 }}>
            Select a date and mark attendance or leave for the class
          </ArgonTypography>
        </ArgonBox>
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white !important",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <ArgonBox display="flex" flexDirection="column" gap={3}>
          {/* Form Type Selection */}
          <ArgonBox>
            <FormControl component="fieldset">
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, mt: 1, display: "block" }}
              >
                Select Type
              </ArgonTypography>
              <RadioGroup
                row
                value={formType}
                onChange={(e) => {
                  setFormType(e.target.value);
                  setFormData((prev) => ({ ...prev, topicCovered: "", leaveReason: "" }));
                }}
                sx={{ gap: 2, ml: 1.4 }}
              >
                <FormControlLabel
                  value="attendance"
                  control={
                    <Radio
                      sx={{
                        color: "#4CAF50",
                        "&.Mui-checked": {
                          color: "#4CAF50",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  }
                  label={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <CheckCircle sx={{ fontSize: 18, color: "#4CAF50" }} />
                      <ArgonTypography variant="button" fontWeight="medium">
                        MARK ATTENDANCE
                      </ArgonTypography>
                    </ArgonBox>
                  }
                  sx={{
                    border: formType === "attendance" ? "2px solid #4CAF50" : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    mr: 0,
                    backgroundColor: formType === "attendance" ? "#f1f8f4" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                />
                <FormControlLabel
                  value="leave"
                  control={
                    <Radio
                      sx={{
                        color: "#f44336",
                        "&.Mui-checked": {
                          color: "#f44336",
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  }
                  label={
                    <ArgonBox display="flex" alignItems="center" gap={1}>
                      <EventBusy sx={{ fontSize: 18, color: "#f44336" }} />
                      <ArgonTypography variant="button" fontWeight="medium">
                        MARK LEAVE
                      </ArgonTypography>
                    </ArgonBox>
                  }
                  sx={{
                    border: formType === "leave" ? "2px solid #f44336" : "1px solid #e0e0e0",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    backgroundColor: formType === "leave" ? "#fef5f5" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                />
              </RadioGroup>
            </FormControl>
          </ArgonBox>

          {/* Date Selection - Different UI for Attendance vs Leave */}
          {formType === "attendance" ? (
            <>
              {/* New Attendance - Calendar Date Picker and Type Selection */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Select Dates *
                </ArgonTypography>

                {/* Date Input with Add Button */}
                <ArgonBox display="flex" gap={1} mb={2} alignItems="center">
                  <ArgonBox sx={{ width: "100%" }}>
                    <CustomDatePicker
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue)}
                      width="100%"
                      maxDate={dayjs()}
                      format="DD/MM/YYYY"
                    />
                  </ArgonBox>
                  <ArgonButton
                    variant="gradient"
                    color="info"
                    onClick={() => {
                      if (selectedDate) {
                        const dateString = selectedDate.format("YYYY-MM-DD");
                        if (!selectedDates.includes(dateString)) {
                          setSelectedDates([...selectedDates, dateString]);
                          setSelectedDate(null);
                        } else {
                          toast.warning("Date already added");
                        }
                      }
                    }}
                    sx={{
                      minWidth: "120px",
                      height: "45px",
                      flexShrink: 0,
                    }}
                  >
                    Add Date
                  </ArgonButton>
                </ArgonBox>

                {/* Selected Dates List */}
                {selectedDates.length > 0 && (
                  <ArgonBox
                    sx={{
                      backgroundColor: "#f8f9fa",
                      borderRadius: "8px",
                      p: 2,
                      border: "1px solid #e0e0e0",
                      mb: 2,
                    }}
                  >
                    <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                      Selected Dates ({selectedDates.length}):
                    </ArgonTypography>
                    <ArgonBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                      {selectedDates.map((date, index) => (
                        <ArgonBox
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            backgroundColor: "white",
                            border: "1px solid #5e72e4",
                            borderRadius: "8px",
                            px: 2,
                            py: 1,
                          }}
                        >
                          <ArgonTypography variant="caption" fontWeight="medium">
                            {new Date(date).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </ArgonTypography>
                          <ArgonButton
                            variant="text"
                            color="error"
                            size="small"
                            onClick={() => {
                              setSelectedDates(selectedDates.filter((d) => d !== date));
                            }}
                            sx={{ minWidth: "auto", p: 0, fontSize: "18px" }}
                          >
                            ×
                          </ArgonButton>
                        </ArgonBox>
                      ))}
                    </ArgonBox>
                  </ArgonBox>
                )}
              </ArgonBox>

              {/* Type Selection Dropdown */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Attendance Type *
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={selectedType}
                  options={[
                    { value: "REGULAR", label: "Regular Class" },
                    { value: "EXTRA", label: "Extra Class" },
                    { value: "RECOVERY", label: "Recovery Class" },
                  ]}
                  onChange={(e) => setSelectedType(e.target.value)}
                  width="100%"
                />
              </ArgonBox>

              {/* Optional Start and End Time */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Start Time (Optional)
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 45,
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      End Time (Optional)
                    </ArgonTypography>
                    <TextField
                      fullWidth
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: 45,
                          borderRadius: "8px",
                        },
                      }}
                    />
                  </ArgonBox>
                </Grid>
              </Grid>
            </>
          ) : (
            /* Calendar Date Picker for Leave - Multiple dates */
            <ArgonBox>
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                color="text"
                sx={{ mb: 1, display: "block" }}
              >
                Select Leave Dates *
              </ArgonTypography>

              {/* Date Input with Add Button */}
              <ArgonBox display="flex" gap={1} mb={2} alignItems="center">
                <ArgonBox sx={{ width: "100%" }}>
                  <CustomDatePicker
                    value={newLeaveDate}
                    onChange={(newValue) => setNewLeaveDate(newValue)}
                    width="100%"
                    maxDate={dayjs()}
                    format="DD/MM/YYYY"
                  />
                </ArgonBox>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    if (newLeaveDate) {
                      const dateString = newLeaveDate.format("YYYY-MM-DD");
                      if (!selectedLeaveDates.includes(dateString)) {
                        setSelectedLeaveDates([...selectedLeaveDates, dateString]);
                        setNewLeaveDate(null);
                      } else {
                        toast.warning("Date already added");
                      }
                    }
                  }}
                  sx={{
                    minWidth: "120px",
                    height: "45px",
                    flexShrink: 0,
                  }}
                >
                  Add Date
                </ArgonButton>
              </ArgonBox>

              {/* Selected Dates List */}
              {selectedLeaveDates.length > 0 && (
                <ArgonBox
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    p: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <ArgonTypography variant="caption" fontWeight="medium" color="text" mb={1}>
                    Selected Dates ({selectedLeaveDates.length}):
                  </ArgonTypography>
                  <ArgonBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                    {selectedLeaveDates.map((date, index) => (
                      <ArgonBox
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: "white",
                          border: "1px solid #5e72e4",
                          borderRadius: "8px",
                          px: 2,
                          py: 1,
                        }}
                      >
                        <ArgonTypography variant="caption" fontWeight="medium">
                          {new Date(date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </ArgonTypography>
                        <ArgonButton
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            setSelectedLeaveDates(selectedLeaveDates.filter((d) => d !== date));
                          }}
                          sx={{ minWidth: "auto", p: 0, fontSize: "18px" }}
                        >
                          ×
                        </ArgonButton>
                      </ArgonBox>
                    ))}
                  </ArgonBox>
                </ArgonBox>
              )}
            </ArgonBox>
          )}

          {/* Conditional Fields Based on Form Type */}
          {formType === "attendance" ? (
            <>
              {/* Topic Covered */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Topics Covered *
                </ArgonTypography>
                <ArgonInput
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter topics discussed in this session..."
                  value={formData.topicCovered}
                  onChange={(e) => handleChange("topicCovered", e.target.value)}
                />
              </ArgonBox>

              {/* File Uploads */}
              <Grid container spacing={2}>
                {/* Homework Upload */}
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Homework (Optional)
                    </ArgonTypography>
                    <ArgonBox
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d2d6da",
                        borderRadius: 2,
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#5e72e4",
                          background: "#f8f9fe",
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                      <ArgonTypography variant="caption" color="text" textAlign="center">
                        {formData.homeWork ? formData.homeWork.name : "Upload PDF/Image"}
                      </ArgonTypography>
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange("homeWork", e.target.files[0])}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Voice Note Upload */}
                <Grid item xs={12} sm={6}>
                  <ArgonBox>
                    <ArgonTypography
                      variant="button"
                      fontWeight="medium"
                      color="text"
                      sx={{ mb: 1, display: "block" }}
                    >
                      Voice Note (Optional)
                    </ArgonTypography>
                    <ArgonBox
                      component="label"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px dashed #d2d6da",
                        borderRadius: 2,
                        p: 2.5,
                        cursor: "pointer",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "#5e72e4",
                          background: "#f8f9fe",
                        },
                      }}
                    >
                      <CloudUpload sx={{ fontSize: 32, color: "#94a3b8", mb: 1 }} />
                      <ArgonTypography variant="caption" color="text" textAlign="center">
                        {formData.voiceNote ? formData.voiceNote.name : "Upload Audio"}
                      </ArgonTypography>
                      <input
                        type="file"
                        hidden
                        accept="audio/*"
                        onChange={(e) => handleFileChange("voiceNote", e.target.files[0])}
                      />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>
            </>
          ) : (
            /* Leave Section */
            <>
              {/* Leave For Dropdown */}
              <ArgonBox>
                <ArgonTypography
                  variant="button"
                  fontWeight="medium"
                  color="text"
                  sx={{ mb: 1, display: "block" }}
                >
                  Leave For *
                </ArgonTypography>
                <Dropdown
                  label=""
                  value={leaveFor}
                  options={[
                    { value: "TUTOR", label: "Tutor" },
                    { value: "PARENT", label: "Parent" },
                  ]}
                  onChange={(e) => setLeaveFor(e.target.value)}
                  width="100%"
                />
              </ArgonBox>
            </>
          )}
        </ArgonBox>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <ArgonButton
          onClick={handleClose}
          variant="outlined"
          color="secondary"
          disabled={loading}
          fullWidth
        >
          Cancel
        </ArgonButton>
        <ArgonButton
          variant="gradient"
          color={formType === "attendance" ? "success" : "error"}
          onClick={handleSubmit}
          disabled={
            loading ||
            (formType === "attendance"
              ? selectedDates.length === 0 || !selectedType
              : selectedLeaveDates.length === 0)
          }
          fullWidth
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : formType === "attendance" ? (
            "Submit Attendance"
          ) : (
            "Submit Leave"
          )}
        </ArgonButton>
      </DialogActions>
    </Dialog>
  );
};

MarkAttendanceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
  classId: PropTypes.string,
};

export default MarkAttendanceModal;
